import PropTypes from 'prop-types';
import {
  assocPath,
  compose,
  either,
  filter,
  flatten,
  groupBy,
  isEmpty,
  isNil,
  map,
  nth,
  of,
  path,
  pathOr,
  prop,
  sum,
  toString
} from 'ramda';
import { DashedTitle, PricingTable, QuoteTable } from '@fasstech/middle-design-system';
import { mapIndexed } from '@@serverHelpers/index.mjs';

const DisplaySelectedOffers = ({
  packs,
  title = '',
  enableSelection,
  onSelect,
  isViewModeCustomer,
  selectedModuleCode = '',
  currentIndex = 0,
  tabs = []
}) => {
  const round = (value) => Math.round(value * 100) / 100;
  const buildOptions = (pricings) => {
    const basePricings = filter(
      ({ isPreSelected, supplementaryCover }) =>
        isPreSelected && either(isNil, isEmpty)(supplementaryCover)
    )(pricings);

    const groupedRenforts = groupBy(prop('supplementaryCover'), pricings);

    const getPrice = (pricing, property) => pathOr(0, ['prices', 0, property], pricing);

    return map((basePricing) => {
      const moduleCode = prop('moduleCode', basePricing);
      const basePrice = round(getPrice(basePricing, 'defaultPrice'));
      const renforts = filter(({ isPreSelected }) => isPreSelected)(
        pathOr([], [moduleCode], groupedRenforts)
      );

      const renfortPrices =
        renforts.length === 0
          ? []
          : renforts.length > 1
            ? map((renfort) => round(getPrice(renfort, 'defaultPrice')), renforts)
            : [round(getPrice(renforts[0], 'defaultPrice')) || 0];

      const renfortInitialPrices =
        renforts.length === 0
          ? []
          : renforts.length > 1
            ? map((renfort) => round(getPrice(renfort, 'initialPrice')), renforts)
            : [round(getPrice(renforts[0], 'initialPrice')) || 0];

      const renfortNames = map(prop('moduleLabel'), renforts);

      const formattedRenfortName = renfortNames.length === 1 ? renfortNames[0] : renfortNames;

      const monthlyFee = round(sum([basePrice, ...renfortPrices]));
      const annualFee = round(monthlyFee * 12);

      const initialPrice = getPrice(basePricing, 'initialPrice');

      return {
        moduleCode,
        title: prop('moduleLabel', basePricing),
        basePrice,
        renfortPrice: renfortPrices,
        renfortInitialPrice: renfortInitialPrices,
        renfortName: formattedRenfortName,
        initialPrice: basePrice === initialPrice ? null : initialPrice, // On affiche le prix initial uniquement s'il est différent du prix final
        monthlyFee,
        annualFee
      };
    }, basePricings);
  };

  const pricingsOptions = !isViewModeCustomer
    ? flatten(
      mapIndexed((pack, index) => {
        const options = buildOptions(pack.pricings);
        return compose(
          assocPath([0, 'className'], pathOr('', [index, 'className'], tabs)),
          assocPath([0, 'moduleCode'], `${index}-${path([0, 'moduleCode'])}`)
        )(options);
      }, packs)
    )
    : buildOptions(packs[0].pricings);

  const totalMonthlyFee = toString(round(sum(map(prop('monthlyFee'), pricingsOptions))));
  const totalAnnualFee = toString(round(sum(map(prop('annualFee'), pricingsOptions))));

  return (
    <div className={`${isViewModeCustomer ? 'flex flex-col' : 'grid'}  justify-center gap-4`}>
      {isViewModeCustomer && (
        <DashedTitle className={'flex justify-center items-center pt-4'}>{title}</DashedTitle>
      )}

      <div className="md:flex hidden justify-center">
        <PricingTable
          options={pricingsOptions}
          labels={['Base', 'Renfort', 'Cotisation mensuelle', 'Cotisation annuelle']}
          onSelect={onSelect}
          showButtons={enableSelection}
          selectedLevel={selectedModuleCode}
        />
      </div>
      {/* Mobile */}
      <div className="md:hidden relative w-full">
        <PricingTable
          options={!isViewModeCustomer ? of(nth(currentIndex, pricingsOptions)) : pricingsOptions}
          labels={['Base', 'Renfort', 'Cotisation mensuelle', 'Cotisation annuelle']}
          onSelect={onSelect}
          showButtons={enableSelection}
          selectedLevel={selectedModuleCode}
        />
      </div>
      {!isViewModeCustomer && (
        <QuoteTable
          title={'Montant total de cotisation'}
          iconName={'calculatorFilled'}
          rows={[
            {
              label: 'Montant cotisation',
              monthlyAmount: totalMonthlyFee,
              annualAmount: totalAnnualFee
            }
          ]}
        />
      )}
    </div>
  );
};

DisplaySelectedOffers.propTypes = {
  packs: PropTypes.array,
  onSelection: PropTypes.func,
  enableSelection: PropTypes.bool,
  title: PropTypes.string,
  viewMode: PropTypes.string,
  selectedModuleCode: PropTypes.string
};

export default DisplaySelectedOffers;
